import { FormState } from '@vtex/admin-ui-form'
import constate from 'constate'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { RestrictionsMap } from '../typings'

export type RestrictionElementProps = {
  name: string
  form: FormState
}

export type RestrictionFieldProps = {
  name: string
}

export type RestrictionConfig = {
  Element: (props: RestrictionElementProps) => ReactNode
  props: RestrictionFieldProps
  hidden?: boolean
  path: string
}

type RegisteredRestrictionsType = Record<RestrictionsMap, RestrictionConfig>

const useRestrictions = () => {
  const [registeredRestrictions, setRegisteredRestrictions] =
    useState<RegisteredRestrictionsType>({} as RegisteredRestrictionsType)

  const register = useCallback((config: RestrictionConfig) => {
    const {
      props: { name },
    } = config

    setRegisteredRestrictions((prev) => {
      const registered = !!prev[name]

      if (registered) {
        return prev
      }

      return { ...prev, [name]: config }
    })
  }, [])

  const hide = useCallback((name: string) => {
    setRegisteredRestrictions((prev) => {
      const registered = !!prev[name]

      if (registered) {
        return {
          ...prev,
          [name]: { ...prev[name], hidden: true },
        }
      }

      return prev
    })
  }, [])

  const show = useCallback((name: string) => {
    setRegisteredRestrictions((prev) => {
      const registered = !!prev[name]

      if (registered) {
        return {
          ...prev,
          [name]: { ...prev[name], hidden: false },
        }
      }

      return prev
    })
  }, [])

  const visibleRestrictions: Array<RestrictionConfig> = useMemo(
    () =>
      Object.entries(registeredRestrictions)
        .filter(([, restrictionValue]) => !restrictionValue.hidden)
        .map(([, restrictionValue]) => restrictionValue),
    [registeredRestrictions]
  )

  return {
    registeredRestrictions,
    visibleRestrictions,
    register,
    hide,
    show,
  }
}

const [
  RestrictionsProvider,
  useRestrictionsContext,
  useRegisterContext,
  useRestrictionVisibility,
] = constate(
  useRestrictions,
  (value) => ({
    registeredRestrictions: value.registeredRestrictions,
    visibleRestrictions: value.visibleRestrictions,
  }),
  (value) => ({
    register: value.register,
  }),
  (value) => ({
    hide: value.hide,
    show: value.show,
  })
)

export {
  RestrictionsProvider,
  useRegisterContext,
  useRestrictionVisibility,
  useRestrictionsContext,
}
